import React, { useEffect, useState, useMemo } from 'react';
import { downloadLinksService } from '../services/api';
import type { DownloadLinkItem } from '../types';
import { useLanguage } from '../context/LanguageContext';
import { useSiteConfig } from '../context/SiteConfigContext';
import { mergeDownloadPageConfig, pickLocalizedText } from '../utils/downloadPageText';
import '../styles/Download.css';

const Download: React.FC = () => {
    const [links, setLinks] = useState<DownloadLinkItem[]>([]);
    const [loading, setLoading] = useState(true);
    const { t, locale } = useLanguage();
    const { config } = useSiteConfig();
    const downloadPage = useMemo(
        () => mergeDownloadPageConfig(config.download),
        [config.download]
    );
    const dt = (key: keyof typeof downloadPage, fallbackKey: string) =>
        pickLocalizedText(downloadPage[key], locale, t(fallbackKey));

    useEffect(() => {
        const load = async () => {
            try {
                const data = await downloadLinksService.getList();
                setLinks(Array.isArray(data) ? data : []);
            } catch {
                setLinks([]);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const systemRequirements = useMemo(() => ({
        minimum: {
            title: t('download.minimum'),
            subtitle: t('download.lowSettings'),
            specs: [
                { label: t('download.operatingSystem'), value: '64-bit Windows 10 / Windows 11' },
                { label: t('download.processor'), value: 'Intel Core i5-8500 3.0GHz 6-Core / Ryzen 5 3500 3.6 GHz 6-Core' },
                { label: t('download.ram'), value: '16 GB RAM' },
                { label: t('download.gpu'), value: 'NVIDIA GeForce GTX 960 / AMD Radeon RX 460' },
                { label: t('download.directX'), value: 'Version 11 / 12' },
                { label: t('download.storage'), value: '50 GB of available space' }
            ]
        },
        recommended: {
            title: t('download.suggested'),
            subtitle: t('download.highQuality'),
            specs: [
                { label: t('download.operatingSystem'), value: '64-bit Windows 10 / Windows 11' },
                { label: t('download.processor'), value: 'Intel i7-10700 2.90 GHz 8-Core / AMD Ryzen 7 3700X 3.6GHz 8-Core' },
                { label: t('download.ram'), value: '16 GB RAM' },
                { label: t('download.gpu'), value: 'NVIDIA Geforce GTX 1070 / AMD RX 580' },
                { label: t('download.directX'), value: 'Version 11 / 12' },
                { label: t('download.storage'), value: '50 GB SSD' }
            ]
        }
    }), [t]);

    const hasValidUrl = (url: string) => url && url.trim() !== '' && url !== '#';

    return (
        <div className="download-page">
            <div className="download-header">
                <div className="free-badge">{dt('freeBadge', 'download.free')}</div>
                <h1>{dt('title', 'download.title')}</h1>
                <p>{dt('subtitle', 'download.subtitle')}</p>
                <span className="client-desc">{dt('clientDesc', 'download.clientDesc')}</span>
            </div>

            <div className="download-main">
                <div className="download-button-section">
                    {loading ? (
                        <p className="download-loading">{t('download.loading')}</p>
                    ) : links.length > 0 ? (
                        <div className="download-links-list">
                            {links.map((link) =>
                                hasValidUrl(link.url) ? (
                                    <a
                                        key={link.id ?? link.title}
                                        href={link.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-primary btn-xl download-btn"
                                    >
                                        {link.title}
                                    </a>
                                ) : (
                                    <span key={link.id ?? link.title} className="btn btn-primary btn-xl download-btn download-btn-disabled" title={link.description || undefined}>
                                        {link.title}
                                    </span>
                                )
                            )}
                        </div>
                    ) : (
                        <a
                            href="#"
                            className="btn btn-primary btn-xl download-btn download-btn-disabled"
                            onClick={(e) => e.preventDefault()}
                        >
                            {t('download.downloadGameDefault')}
                        </a>
                    )}
                </div>
            </div>

            <div className="requirements-section">
                <h2>{dt('systemRequirementsTitle', 'download.systemRequirements')}</h2>
                <p>{dt('systemRequirementsNote', 'download.systemRequirementsNote')}</p>

                <div className="requirements-grid">
                    {Object.entries(systemRequirements).map(([key, req]) => (
                        <div key={key} className="requirements-card">
                            <h3>{req.title}</h3>
                            <h4>{req.subtitle}</h4>
                            <div className="specs-list">
                                {req.specs.map((spec, index) => (
                                    <div key={index} className="spec-item">
                                        <span className="spec-label">{spec.label}</span>
                                        <span className="spec-value">{spec.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Download;
